import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { ScanFace, SlidersHorizontal, QrCode, Globe, ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'

export default function LandingPage() {
  const [lang, setLang] = useState<'en' | 'nl'>('en')
  const [images, setImages] = useState<string[]>([])
  const [currentIndex, setCurrentIndex] = useState(0)

  useEffect(() => {
    fetch('/api/carousel')
      .then(res => res.json())
      .then(data => setImages(data))
      .catch(err => console.error('Failed to fetch carousel images', err))
  }, [])

  useEffect(() => {
    if (images.length === 0) return
    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % images.length)
    }, 5000)
    return () => clearInterval(timer)
  }, [images.length])

  const content = {
    en: {
      title: 'Photo booth software for events',
      subtitle: 'Run photo, GIF, boomerang and video booths from one admin panel. Guests get their pictures by QR code or email.',
      getStarted: 'Get Started',
      featuresTitle: 'What it does',
      features: [
        { icon: ScanFace, title: 'Face tracking', desc: 'Digital props that follow the guest\'s face.' },
        { icon: SlidersHorizontal, title: 'Effects', desc: 'Beauty filter, green screen, LUTs and overlays.' },
        { icon: QrCode, title: 'Sharing', desc: 'QR code, online gallery, email and SMS.' },
        { icon: Globe, title: 'Runs in a browser', desc: 'Works on iPads, laptops and large touch screens. A desktop kiosk client is optional.' },
      ],
      footer: 'PoseBox',
    },
    nl: {
      title: 'Fotoboothsoftware voor evenementen',
      subtitle: 'Draai foto-, GIF-, boomerang- en videobooths vanuit één beheerpaneel. Gasten ontvangen hun foto via QR-code of e-mail.',
      getStarted: 'Aan de slag',
      featuresTitle: 'Wat het doet',
      features: [
        { icon: ScanFace, title: 'Gezichtsherkenning', desc: 'Digitale props die het gezicht van de gast volgen.' },
        { icon: SlidersHorizontal, title: 'Effecten', desc: 'Beautyfilter, green screen, LUTs en overlays.' },
        { icon: QrCode, title: 'Delen', desc: 'QR-code, online galerij, e-mail en sms.' },
        { icon: Globe, title: 'Draait in de browser', desc: 'Werkt op iPads, laptops en grote touchscreens. Een desktop-kioskclient is optioneel.' },
      ],
      footer: 'PoseBox',
    }
  }

  const c = content[lang]

  return (
    <div className="relative flex min-h-screen flex-col items-center justify-center overflow-hidden bg-[#0b0b0a] text-white">
      {/* Language Selector */}
      <div className="absolute top-6 right-6 z-20 flex gap-4">
        <button 
          onClick={() => setLang('en')} 
          className={`text-sm font-medium transition-opacity ${lang === 'en' ? 'opacity-100' : 'opacity-40 hover:opacity-70'}`}
          title="English"
        >
          EN
        </button>
        <button 
          onClick={() => setLang('nl')} 
          className={`text-sm font-medium transition-opacity ${lang === 'nl' ? 'opacity-100' : 'opacity-40 hover:opacity-70'}`}
          title="Nederlands"
        >
          NL
        </button>
      </div>



      {/* Content */}
      <div className="relative z-10 flex flex-col items-center px-6 max-w-5xl">
        {/* Logo */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="mb-6 flex items-center gap-3"
        >
          <h1>
            <img src="/logo-dark.png" alt="PoseBox" className="h-16 w-auto" />
          </h1>
        </motion.div>

        <motion.h2
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.8 }}
          className="text-5xl font-bold tracking-tight text-center mb-4 text-white"
        >
          {c.title}
        </motion.h2>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4, duration: 0.8 }}
          className="text-center text-xl text-white/60 mb-12 max-w-2xl"
        >
          {c.subtitle}
        </motion.p>
        
        {/* Get Started Button (Top) */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 0.6 }}
          className="mb-8"
        >
          <Link
            to="/login"
            className="group relative inline-flex items-center gap-2 rounded-lg bg-primary px-6 py-3 text-base font-semibold text-primary-foreground hover:bg-primary/90 transition-colors"
          >
            {c.getStarted}
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Link>
        </motion.div>

        {/* Grid for Content */}
        <div className="w-full max-w-6xl">
          {/* Content Row */}
          <div className="grid grid-cols-1 lg:grid-cols-[2fr_3fr] gap-8 items-stretch">
            {/* Left Column: Features */}
            <div className="flex flex-col gap-4">
              <h3 className="text-xl font-bold text-white/80 text-center lg:text-left">{c.featuresTitle}</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 h-full content-start">
              {c.features.map((feat, i) => (
                <motion.div
                  key={feat.title}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.8 + i * 0.1, duration: 0.6 }}
                  className="bg-white/[0.02] border border-white/10 rounded-lg p-4 hover:bg-white/[0.04] transition-colors flex flex-col justify-between"
                >
                  <div>
                    <div className="h-8 w-8 rounded-md bg-white/5 flex items-center justify-center mb-3">
                      <feat.icon className="h-4 w-4 text-primary" />
                    </div>
                    <h4 className="text-sm font-semibold mb-1">{feat.title}</h4>
                    <p className="text-xs text-white/40">{feat.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Right Column: Carousel */}
            <div className="flex flex-col gap-4 w-full h-full">
              <h3 className="text-xl font-bold text-white/80 text-center lg:text-left">Preview</h3>
              {images.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 1.2, duration: 0.6 }}
                  className="relative w-full h-full min-h-[300px] lg:min-h-full overflow-hidden rounded-lg border border-white/10 bg-black/30"
                >
                  <AnimatePresence mode="wait">
                    <motion.img
                      key={currentIndex}
                      src={images[currentIndex]}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.5 }}
                      className="absolute inset-0 h-full w-full object-contain"
                      alt="Carousel"
                    />
                  </AnimatePresence>
                  
                  {images.length > 1 && (
                    <>
                      <button
                        onClick={() => setCurrentIndex((prev) => (prev - 1 + images.length) % images.length)}
                        className="absolute left-2 top-1/2 -translate-y-1/2 p-1.5 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors"
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => setCurrentIndex((prev) => (prev + 1) % images.length)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors"
                      >
                        <ChevronRight className="h-4 w-4" />
                      </button>
                      <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1">
                        {images.map((_, i) => (
                          <div
                            key={i}
                            className={`h-1 w-1 rounded-full ${i === currentIndex ? 'bg-white' : 'bg-white/30'}`}
                          />
                        ))}
                      </div>
                    </>
                  )}
                </motion.div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5, duration: 0.8 }}
          className="mt-20 text-xs text-white/20"
        >
          {c.footer}
        </motion.div>
      </div>
    </div>
  )
}
